import { componentUsable, componentStale, validFeed } from './youtube-feed-contract.js';

const instances = new WeakMap();
const compact = (value) => new Intl.NumberFormat('en-GB', { notation: 'compact', maximumSignificantDigits: 3 }).format(value);
const duration = (seconds) => seconds >= 3600
  ? `${Math.floor(seconds / 3600)}:${String(Math.floor(seconds % 3600 / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
  : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export function feedView(snapshot, now = Date.now()) {
  if (!validFeed(snapshot)) return { subscribers: null, featured: [], members: [], stale: true };
  const parts = snapshot.components;
  return {
    subscribers: componentUsable(parts.subscribers, now) ? parts.subscribers.value : null,
    featured: componentUsable(parts.featured, now) ? parts.featured.value : [],
    members: componentUsable(parts.members, now) ? parts.members.value : [],
    stale: Object.values(parts).some((part) => componentStale(part, now)),
  };
}

function thumbnail(image, video) {
  const sources = [...new Set([video.thumbnail, ...video.thumbnails])];
  let index = 0;
  image.alt = video.title;
  image.removeAttribute('srcset');
  image.onerror = () => {
    if (++index < sources.length) image.src = sources[index];
    else { image.hidden = true; image.dataset.imageUnavailable = 'true'; }
  };
  image.hidden = false;
  image.src = sources[0];
}

export function renderFeed(section, snapshot, { now = Date.now(), state = {} } = {}) {
  const view = feedView(snapshot, now);
  const doc = section.ownerDocument;
  doc.querySelectorAll('[data-youtube-subscriber-count]').forEach((element) => {
    element.dataset.youtubeFallback ||= element.textContent;
    element.textContent = view.subscribers === null ? element.dataset.youtubeFallback
      : element.dataset.youtubeSubscriberTemplate.replace('{count}', compact(view.subscribers));
  });
  const focusedVideo = doc.activeElement?.dataset?.videoId;
  const episodes = section.querySelector('[data-youtube-episodes]');
  const player = section.querySelector('#CinemaVaultPlayer');
  const select = (video, userInitiated = false) => {
    state.selected = video.videoId;
    state.interacted ||= userInitiated;
    if (userInitiated) player.dataset.privacyPlayRequest = 'true';
    player.dataset.privacySrc = `https://www.youtube-nocookie.com/embed/${video.videoId}${userInitiated ? '?autoplay=1' : ''}`;
    player.title = video.title;
    player.dataset.privacyPoster = video.thumbnail;
    episodes.querySelectorAll('[role="tab"]').forEach((button) => {
      const active = button.dataset.videoId === state.selected;
      button.setAttribute('aria-selected', String(active));
      button.classList.toggle('home-cinematic-vault__episode--active', active);
    });
  };
  const nextEpisodes = view.featured.map((video, index) => {
    const button = section.querySelector('[data-youtube-episode-template]').content.firstElementChild.cloneNode(true);
    button.dataset.videoId = video.videoId;
    button.dataset.youtubeFeaturedSlot = String(index);
    button.querySelector('[data-youtube-video-title]').textContent = video.title;
    button.querySelector('[data-youtube-video-meta]').textContent = index === 0 ? 'Latest release' : `Top viewed // ${compact(video.viewCount)} views`;
    button.querySelector('[data-youtube-video-time]').textContent = duration(video.durationSeconds);
    thumbnail(button.querySelector('img'), video);
    button.setAttribute('aria-selected', String(video.videoId === state.selected));
    button.classList.toggle('home-cinematic-vault__episode--active', video.videoId === state.selected);
    button.addEventListener('click', () => select(video, true));
    button.addEventListener('keydown', (event) => {
      const tabs = [...episodes.children];
      const offset = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 0;
      if (offset) { event.preventDefault(); tabs[(index + offset + tabs.length) % tabs.length].focus(); }
    });
    return button;
  });
  episodes.replaceChildren(...nextEpisodes);
  section.querySelector('[data-youtube-featured-panel]').hidden = !view.featured.length && !state.interacted;
  section.querySelector('[data-youtube-featured-fallback]').hidden = Boolean(view.featured.length);
  if (view.featured.length && !state.interacted && state.selected !== view.featured[0].videoId) select(view.featured[0]);
  if (!view.featured.length && !state.interacted) { player.removeAttribute('src'); player.removeAttribute('data-privacy-src'); state.selected = null; }
  if (focusedVideo) nextEpisodes.find((button) => button.dataset.videoId === focusedVideo)?.focus();
  if (view.featured[0]) doc.querySelectorAll('[data-youtube-latest-thumbnail]').forEach((image) => thumbnail(image, view.featured[0]));

  const memberSignature = JSON.stringify(view.members);
  if (state.memberSignature !== memberSignature) {
    const track = section.querySelector('[data-members-track]');
    track.replaceChildren(...view.members.map((video, index) => {
      const card = section.querySelector('[data-youtube-member-template]').content.firstElementChild.cloneNode(true);
      card.dataset.youtubeMembersSlot = String(index);
      card.querySelector('[data-member-link]').href = `https://www.youtube.com/watch?v=${video.videoId}`;
      card.querySelector('h4').textContent = video.title;
      card.querySelector('.uncut-card__meta-row span').textContent = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(video.publishedAt));
      card.querySelector('.uncut-card__meta-row strong').textContent = duration(video.durationSeconds);
      thumbnail(card.querySelector('img'), video);
      return card;
    }));
    state.memberSignature = memberSignature;
  }
  section.querySelector('[data-members-carousel]').hidden = !view.members.length;
  const status = section.querySelector('[data-youtube-feed-status]');
  status.hidden = section.dataset.youtubeQa !== 'true';
  status.textContent = view.stale ? 'YouTube feed unavailable or more than two hours old. Verified content expires after 24 hours.'
    : `YouTube source checked ${new Date(snapshot.attemptedAt).toLocaleString('en-GB')}.`;
  section.dataset.youtubeFeedState = view.stale ? 'stale' : 'fresh';
  return view;
}

export async function initializeYouTubeFeed(section, { fetchImpl = globalThis.fetch, storage } = {}) {
  if (instances.has(section)) return;
  const state = {};
  const view = section.ownerDocument.defaultView;
  state.onBlur = () => {
    if (section.ownerDocument.activeElement === section.querySelector('#CinemaVaultPlayer')) state.interacted = true;
  };
  view?.addEventListener('blur', state.onBlur);
  instances.set(section, state);
  try { storage ??= globalThis.localStorage; } catch { /* Storage is optional. */ }
  const endpoint = section.dataset.youtubeFeed;
  const cacheKey = `urbandoned:youtube-feed:v1:${endpoint}`;
  let snapshot;
  try { const cached = JSON.parse(storage?.getItem(cacheKey)); if (validFeed(cached)) snapshot = cached; } catch { /* Optional cache. */ }
  renderFeed(section, snapshot, { state });
  async function refresh() {
    if (!section.isConnected) return;
    try {
      const url = new URL(endpoint, section.ownerDocument.location.href);
      if (!endpoint || (url.protocol !== 'https:' && url.origin !== section.ownerDocument.location.origin)) throw new Error('Unconfigured feed');
      const response = await fetchImpl(url.href, { credentials: 'omit', signal: AbortSignal.timeout(5_000) });
      if (!response.ok) throw new Error('Feed unavailable');
      const next = await response.json();
      if (!validFeed(next)) throw new Error('Invalid feed');
      snapshot = next;
      try { storage?.setItem(cacheKey, JSON.stringify(snapshot)); } catch { /* Optional cache. */ }
    } catch { /* Preserve a bounded last-good snapshot. */ }
    renderFeed(section, snapshot, { state });
    if (section.isConnected) state.timer = setTimeout(refresh, 5 * 60 * 1000);
  }
  await refresh();
}

export function initializeYouTubeFeeds(scope = document) {
  scope.querySelectorAll('[data-youtube-feed]').forEach((section) => { void initializeYouTubeFeed(section); });
}

if (typeof document !== 'undefined') {
  initializeYouTubeFeeds();
  document.addEventListener('shopify:section:load', (event) => initializeYouTubeFeeds(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-youtube-feed]').forEach((section) => {
      const state = instances.get(section);
      clearTimeout(state?.timer);
      section.ownerDocument.defaultView?.removeEventListener('blur', state?.onBlur);
      instances.delete(section);
    });
  });
}
